import { TenantCollectionHealth } from "./CoreAPI";

/**
 * Backlog direction, used to identify tenants where collection is healthy but
 * intake is not keeping up.
 *
 * There is no server-side flag for this. Unlike `stalled`, which is
 * server-computed and must not be re-derived, "behind" has to be computed
 * client-side — so the rule lives here, tested, rather than inline at a call
 * site. Thresholds are provisional pending confirmation against real data.
 *
 * PHASE-2 SURFACE. Everything below except `SNAPSHOT_INTERVAL_SECONDS`,
 * `SLIPPING_RUN` and `BEHIND_RUN` — `risingRun`, `backlogTrend` itself, and
 * the `BacklogTrend`/`BehindLevel` types — has no non-test importer yet.
 * Phase 1 passes an always-empty `behindRuns` map (see index.tsx), so none of
 * it runs against real data on the page today. It is exercised only by
 * backlogTrend.test.ts, kept deliberately: this is the seam phase 2's
 * per-tenant history fan-out plugs into, not dead code to delete. See spec §6.
 */

/**
 * Snapshots are collected every 5 minutes, so a run of 3 is roughly 15 minutes.
 *
 * This is a client-side assumption, not read from the server. The run count is
 * unaffected if the real cadence drifts, but `risingSeconds` and any chip label
 * derived from it would silently mis-state elapsed time. If the collector's
 * cadence changes, this must be updated by hand — nothing will fail loudly.
 *
 * Two consumers depend on this value, so a drift affects both silently: the
 * behind-run classification above, and trendSeries' aggregation bucket width
 * (phase 2 trends). Anyone changing this constant should check both.
 */
export const SNAPSHOT_INTERVAL_SECONDS = 5 * 60;

/** ~15 minutes of continuous growth. */
export const SLIPPING_RUN = 3;

/** ~1 hour of continuous growth. */
export const BEHIND_RUN = 12;

export type BehindLevel = "none" | "slipping" | "behind";

export interface BacklogTrend {
    risingRun: number;
    level: BehindLevel;
    /** Elapsed time the run covers, for the chip label. */
    risingSeconds: number;
}

/**
 * Counts consecutive strictly increasing steps at the tail of the series.
 *
 * Strictly increasing, not non-decreasing: a plateau at a high level is a high
 * backlog, not a tenant falling behind. An absent value breaks the run rather
 * than being read across, because nothing was measured there.
 */
export function risingRun(series: Array<number | undefined>): number {
    let run = 0;

    for (let index = series.length - 1; index > 0; index--) {
        const current = series[index];
        const previous = series[index - 1];

        if (current === undefined || previous === undefined || current <= previous) {
            break;
        }
        run += 1;
    }

    return run;
}

/**
 * Classifies backlog direction for one tenant.
 *
 * `series` is that tenant's backlog values ordered oldest to newest.
 * `collectionHealth` gates the result: on anything other than Healthy the
 * freshest point may be stale or missing, so a run is reported but no level is
 * claimed.
 *
 * `level: "none"` is not a positive finding. It covers three different
 * situations: not rising, not rising *yet* within the threshold, and no history
 * to judge from at all. Do not read it as evidence a backlog is stable.
 */
export function backlogTrend(
    series: Array<number | undefined>,
    collectionHealth: TenantCollectionHealth | undefined,
): BacklogTrend {
    const run = risingRun(series);
    const risingSeconds = run * SNAPSHOT_INTERVAL_SECONDS;

    if (collectionHealth !== "Healthy") {
        return { risingRun: run, level: "none", risingSeconds };
    }
    if (run >= BEHIND_RUN) {
        return { risingRun: run, level: "behind", risingSeconds };
    }
    if (run >= SLIPPING_RUN) {
        return { risingRun: run, level: "slipping", risingSeconds };
    }
    return { risingRun: run, level: "none", risingSeconds };
}
